// Lera Web Frontend - Main Entry Point

import { LeraTerminal } from './terminal';
import { LeraWebSocket, ChannelMessage } from './websocket';
import { TtydWebSocket } from './ttyd';
import { ChannelManager } from './channels';
import {
  getToken,
  setToken,
  clearToken,
  isTokenExpired,
  refreshTokenIfStale,
  fetchUser,
  startContainer,
  parseAuthCallback,
  logout,
  User,
} from './auth';
import { renderLoginScreen, injectLoginStyles } from './login';
import { renderSettingsPanel, injectSettingsStyles } from './settings';
import { renderAdminPanel, injectAdminStyles } from './admin';
import { FileManager, injectFileStyles } from './files';
import { GridView } from './grid/view';
import { GridShell } from './grid/shell';
import {
  listProfiles, startProfile, stopProfile,
  selectInitialProfile, rememberProfile, recallProfile, forgetProfile, type Profile,
} from './profiles';
import { renderProfileBar, injectProfileBarStyles } from './profile-bar';
import '@xterm/xterm/css/xterm.css';
import '../styles/terminal.css';

// Check if we're in hosted mode (have API endpoints)
function isHostedMode(): boolean {
  // In hosted mode, we have the /api endpoints
  // In standalone mode, we connect directly to a WebSocket
  return window.location.pathname === '/' && !window.location.search.includes('host=');
}

// Get WebSocket URL for standalone mode
function getStandaloneWebSocketUrl(): string {
  const params = new URLSearchParams(window.location.search);
  const host = params.get('host') || window.location.hostname || 'localhost';
  const port = params.get('port') || '8080';
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${protocol}//${host}:${port}`;
}

// Get WebSocket URL for hosted mode (via Caddy proxy)
function getHostedWebSocketUrl(port: number): string {
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${protocol}//${window.location.host}/ws/${port}`;
}

function escapeHtml(text: string): string {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

// Show loading state
function showLoading(container: HTMLElement): void {
  container.innerHTML = `
    <div class="loading">
      <div class="spinner"></div>
      <p>Loading...</p>
    </div>
  `;
}

function showGridError(container: HTMLElement, message: string, onLegacy: () => void): void {
  const overlay = document.createElement('section');
  overlay.className = 'grid-error-overlay';
  overlay.setAttribute('role', 'alert');
  const title = document.createElement('h2');
  title.textContent = 'Unable to start native grid';
  const detail = document.createElement('p');
  detail.textContent = message;
  const retry = document.createElement('button');
  retry.type = 'button';
  retry.textContent = 'Retry';
  retry.addEventListener('click', () => window.location.reload());
  const legacy = document.createElement('button');
  legacy.type = 'button';
  legacy.textContent = 'Use legacy renderer';
  legacy.addEventListener('click', onLegacy);
  const actions = document.createElement('div');
  actions.className = 'grid-error-actions';
  actions.append(retry, legacy);
  overlay.append(title, detail, actions);
  container.replaceChildren(overlay);
}

// Main application state
interface AppState {
  token: string | null;
  user: User | null;
  terminal: LeraTerminal | null;
  ws: LeraWebSocket | null;
  ttyd: TtydWebSocket | null;
  workspaceMode: boolean;  // True when using ttyd for workspace mode
  channels: Map<string, ChannelMessage>;  // Named channel data from server
  channelManager: ChannelManager | null;
  commandHistory: string[];  // Input history
  historyIndex: number;  // -1 = not navigating, 0+ = index from end
  fileManager: FileManager | null;
  gridView: GridView | null;
  gridShell: GridShell | null;
  detachProfile: (() => void) | null;
  // Set once native grid mode is abandoned for this session (the user chose
  // the legacy fallback, or negotiation itself failed) -- see
  // retireProfileSwitcher(). Once true, the switcher must never draw, poll,
  // or reattach again.
  profileSwitcherRetired: boolean;
  profilePollTimer: number | null;
}

const state: AppState = {
  token: null,
  user: null,
  terminal: null,
  ws: null,
  ttyd: null,
  workspaceMode: false,
  channels: new Map(),
  channelManager: null,
  commandHistory: [],
  historyIndex: -1,
  fileManager: null,
  gridView: null,
  gridShell: null,
  detachProfile: null,
  profileSwitcherRetired: false,
  profilePollTimer: null,
};

// Once native grid mode is abandoned (legacy fallback chosen, or negotiation
// failed outright), the profile switcher must retire permanently: any stale
// state.detachProfile closure becomes uninvokable, the poll that would
// otherwise redraw the bar is stopped, and (belt and suspenders) drawBar/
// refresh/attach all check the flag directly so nothing can resurrect the
// bar or reattach native grid mode even from a bar node rendered before this
// was called. Called from useLegacy()/failNegotiation() inside
// attachProfile(), and checked from drawBar()/refresh()/attach() inside
// initHostedMode() -- two different function scopes, hence state-based
// rather than a shared closure.
function retireProfileSwitcher(): void {
  state.profileSwitcherRetired = true;
  state.detachProfile = null;
  if (state.profilePollTimer !== null) {
    window.clearInterval(state.profilePollTimer);
    state.profilePollTimer = null;
  }
}

// Initialize the retained workspace/legacy xterm route.
function initLegacyTerminal(container: HTMLElement, wsUrl: string, wsToken?: string, workspaceMode: boolean = false, fileWsUrl?: string) {
  state.workspaceMode = workspaceMode;
  // Clear container
  container.innerHTML = '';

  // Recreate app structure
  const app = document.createElement('div');
  app.id = 'app';
  app.innerHTML = `
    <header id="header">
      <h1>Lera</h1>
      <div class="status-bar">
        <span id="status" class="status disconnected">Disconnected</span>
        <span id="mud-status" class="mud-status disconnected">Not connected to MUD</span>
        <button id="files-btn" class="header-btn" title="Files">
          <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
            <path d="M10 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2h-8l-2-2z"/>
          </svg>
        </button>
        <a href="/docs.html" class="header-btn" title="Lua API Docs">
          <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
            <path d="M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z"/>
          </svg>
        </a>
        ${state.user ? `
          <span class="user-info">${escapeHtml(state.user.display_name)}</span>
          ${state.user.is_admin ? `
            <button id="admin-btn" class="header-btn" title="Admin">
              <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
                <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"/>
              </svg>
            </button>
          ` : ''}
          <button id="settings-btn" class="header-btn" title="Settings">
            <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
              <path d="M19.14 12.94c.04-.31.06-.63.06-.94 0-.31-.02-.63-.06-.94l2.03-1.58c.18-.14.23-.41.12-.61l-1.92-3.32c-.12-.22-.37-.29-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94l-.36-2.54c-.04-.24-.24-.41-.48-.41h-3.84c-.24 0-.43.17-.47.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96c-.22-.08-.47 0-.59.22L2.74 8.87c-.12.21-.08.47.12.61l2.03 1.58c-.04.31-.06.63-.06.94s.02.63.06.94l-2.03 1.58c-.18.14-.23.41-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32c.12-.22.07-.47-.12-.61l-2.01-1.58zM12 15.6c-1.98 0-3.6-1.62-3.6-3.6s1.62-3.6 3.6-3.6 3.6 1.62 3.6 3.6-1.62 3.6-3.6 3.6z"/>
            </svg>
          </button>
        ` : ''}
      </div>
    </header>
    <main id="terminal"></main>
    ${!workspaceMode ? `
    <div id="input-bar">
      <span class="input-prompt">&gt;</span>
      <input id="command-input" type="text" autocomplete="off" autocapitalize="off" spellcheck="false" placeholder="Enter command...">
    </div>
    ` : ''}
  `;
  container.appendChild(app);

  // Settings button handler
  const settingsBtn = app.querySelector('#settings-btn');
  if (settingsBtn && state.token && state.user) {
    injectSettingsStyles();
    settingsBtn.addEventListener('click', () => {
      renderSettingsPanel({
        container: document.body,
        token: state.token!,
        userEmail: state.user!.email,
        onClose: () => {
          state.terminal?.focus();
        },
        onLogout: () => {
          window.location.reload();
        },
        // Legacy/workspace mode has no profile switcher bar to redraw.
        onProfilesChanged: () => {},
      });
    });
  }

  // Admin button handler
  const adminBtn = app.querySelector('#admin-btn');
  if (adminBtn && state.token) {
    injectAdminStyles();
    adminBtn.addEventListener('click', () => {
      renderAdminPanel({
        container: document.body,
        token: state.token!,
        onClose: () => {
          state.terminal?.focus();
        },
      });
    });
  }

  // Files button handler
  // Determine file WS URL: use explicit fileWsUrl, or fall back to terminal wsUrl
  const resolvedFileWsUrl = fileWsUrl || wsUrl;
  const filesBtn = app.querySelector('#files-btn');
  if (filesBtn) {
    injectFileStyles();
    filesBtn.addEventListener('click', () => {
      if (state.fileManager) return;  // Already open

      const termEl = document.getElementById('terminal');
      if (termEl) termEl.style.display = 'none';

      state.fileManager = new FileManager({
        container: app,
        wsUrl: resolvedFileWsUrl,
        token: wsToken,
        onClose: () => {
          state.fileManager = null;
          const termEl2 = document.getElementById('terminal');
          if (termEl2) termEl2.style.display = '';
          state.terminal?.fit();
          state.terminal?.focus();
        },
      });
    });
  }

  const terminalContainer = document.getElementById('terminal')!;
  const statusEl = document.getElementById('status');
  const mudStatusEl = document.getElementById('mud-status');

  // Create legacy/channel sub-containers inside #terminal
  const legacyContainer = document.createElement('div');
  legacyContainer.id = 'legacy-terminal';
  terminalContainer.appendChild(legacyContainer);

  const channelContainer = document.createElement('div');
  channelContainer.id = 'channel-layout';
  channelContainer.style.display = 'none';
  terminalContainer.appendChild(channelContainer);

  // Create channel manager (inactive until layout message received)
  state.channelManager = new ChannelManager(channelContainer, (cols, rows) => {
    state.ws?.sendResize(cols, rows);
  });

  // Create terminal with appropriate input handler
  state.terminal = new LeraTerminal({
    container: legacyContainer,
    onInput: (data) => {
      if (workspaceMode) {
        // Workspace mode: send all input directly to ttyd (it handles echo)
        state.ttyd?.sendInput(data);
      }
      // Non-workspace mode: terminal is display-only, input goes through HTML input
    },
    onResize: (cols, rows) => {
      // Send resize to server when terminal dimensions change
      if (workspaceMode) {
        state.ttyd?.sendResize(cols, rows);
      } else if (!state.channelManager?.isActive) {
        // Only send resize from legacy terminal when channel layout is not active
        state.ws?.sendResize(cols, rows);
      }
    },
  });

  // Set up input bar for non-workspace mode
  const commandInput = document.getElementById('command-input') as HTMLInputElement | null;
  if (commandInput && !workspaceMode) {
    commandInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        const text = commandInput.value;
        state.ws?.sendCommand(text);
        if (text.length > 0) {
          state.commandHistory.push(text);
        }
        state.historyIndex = -1;
        commandInput.value = '';
        e.preventDefault();
      } else if (e.key === 'ArrowUp') {
        if (state.commandHistory.length > 0) {
          if (state.historyIndex === -1) {
            state.historyIndex = state.commandHistory.length - 1;
          } else if (state.historyIndex > 0) {
            state.historyIndex--;
          }
          commandInput.value = state.commandHistory[state.historyIndex];
          // Move cursor to end
          setTimeout(() => commandInput.setSelectionRange(commandInput.value.length, commandInput.value.length), 0);
        }
        e.preventDefault();
      } else if (e.key === 'ArrowDown') {
        if (state.historyIndex >= 0) {
          if (state.historyIndex < state.commandHistory.length - 1) {
            state.historyIndex++;
            commandInput.value = state.commandHistory[state.historyIndex];
          } else {
            state.historyIndex = -1;
            commandInput.value = '';
          }
        }
        e.preventDefault();
      }
    });

    // Click on terminal refocuses input
    terminalContainer.addEventListener('click', () => {
      commandInput.focus();
    });
  }

  // Update connection status
  function updateStatus(connected: boolean) {
    if (statusEl) {
      statusEl.textContent = connected ? 'Connected' : 'Disconnected';
      statusEl.className = `status ${connected ? 'connected' : 'disconnected'}`;
    }
  }

  // Update MUD connection status (legacy mode only)
  function updateMudStatus(host: string | null, port?: number) {
    if (mudStatusEl) {
      if (workspaceMode) {
        // In workspace mode, hide MUD status (shown in terminal)
        mudStatusEl.style.display = 'none';
      } else if (host) {
        mudStatusEl.textContent = `${host}:${port}`;
        mudStatusEl.className = 'mud-status connected';
      } else {
        mudStatusEl.textContent = 'Not connected to MUD';
        mudStatusEl.className = 'mud-status disconnected';
      }
    }
  }

  if (workspaceMode) {
    // Workspace mode: use ttyd WebSocket
    state.ttyd = new TtydWebSocket(wsUrl);

    state.ttyd.on('open', () => {
      updateStatus(true);
      // Send initial terminal size
      if (state.terminal) {
        state.ttyd?.sendResize(state.terminal.cols, state.terminal.rows);
      }
    });

    state.ttyd.on('close', () => {
      updateStatus(false);
    });

    state.ttyd.on('output', (data) => {
      state.terminal?.write(data);
    });

    // Connect
    state.ttyd.connect();
  } else {
    // Legacy mode: use lera WebSocket
    state.ws = new LeraWebSocket(wsUrl);

    state.ws.on('open', () => {
      updateStatus(true);
      state.terminal?.writeln('\x1b[32m[Connected to lera]\x1b[0m');

      // Authenticate if we have a container token
      if (wsToken) {
        state.ws?.authenticate(wsToken);
      }
    });

    state.ws.on('close', () => {
      updateStatus(false);
      state.terminal?.writeln('\x1b[31m[Disconnected from lera]\x1b[0m');
    });

    // Track whether pending text is currently displayed on the terminal line.
    // When output arrives, we must first clear the pending text to avoid duplication.
    let hasPendingText = false;

    state.ws.on('output', (msg) => {
      // Skip legacy output when channel layout is active (output arrives via channels)
      if (state.channelManager?.isActive) return;
      if (hasPendingText) {
        state.terminal?.write('\r\x1b[K');  // Clear pending line before output
        hasPendingText = false;
      }
      state.terminal?.write(msg.data);
    });

    state.ws.on('connected', (msg) => {
      updateMudStatus(msg.host, msg.port);
      if (hasPendingText) {
        state.terminal?.write('\r\x1b[K');
        hasPendingText = false;
      }
      state.terminal?.writeln(`\x1b[32m[MUD connected: ${msg.host}:${msg.port}]\x1b[0m`);
    });

    state.ws.on('disconnected', (msg) => {
      updateMudStatus(null);
      if (hasPendingText) {
        state.terminal?.write('\r\x1b[K');
        hasPendingText = false;
      }
      const reason = msg.reason ? `: ${msg.reason}` : '';
      state.terminal?.writeln(`\x1b[31m[MUD disconnected${reason}]\x1b[0m`);
    });

    state.ws.on('pending', (msg) => {
      // Skip legacy pending when channel layout is active
      if (state.channelManager?.isActive) return;
      state.terminal?.write('\r\x1b[K' + msg.text);
      hasPendingText = msg.text.length > 0;
    });

    state.ws.on('error', (msg) => {
      state.terminal?.writeln(`\x1b[31m[Error: ${msg.code} - ${msg.message}]\x1b[0m`);

      // Handle auth errors
      if (msg.code === 'auth_failed' || msg.code === 'auth_required') {
        clearToken();
        window.location.reload();
      }
    });

    state.ws.on('auth_ok', () => {
      state.terminal?.writeln('\x1b[32m[Authenticated]\x1b[0m');
      // Send terminal size to server after authentication
      if (state.channelManager?.isActive) {
        // Channel layout is active - re-trigger channel resize
        // (the pre-auth resize may have been sent before screen was laid out)
        state.channelManager.triggerResize();
      } else if (state.terminal) {
        state.ws?.sendResize(state.terminal.cols, state.terminal.rows);
      }
    });

    state.ws.on('layout', (msg) => {
      // Switch from legacy single-terminal to channel layout mode
      legacyContainer.style.display = 'none';
      channelContainer.style.display = '';
      // Defer setLayout to next frame so the browser reflows the now-visible container
      requestAnimationFrame(() => {
        state.channelManager?.setLayout(msg.tree);
      });
    });

    state.ws.on('channel', (msg) => {
      state.channels.set(msg.name, msg);
      // Route to channel manager when active
      if (state.channelManager?.isActive) {
        state.channelManager.updateChannel(msg);
      }
    });

    // Connect
    state.ws.connect();
  }

  // Focus appropriate element
  if (commandInput && !workspaceMode) {
    commandInput.focus();
  } else {
    state.terminal.focus();
  }

  // Handle window focus
  window.addEventListener('focus', () => {
    if (commandInput && !workspaceMode) {
      commandInput.focus();
    } else {
      state.terminal?.focus();
    }
  });

  // Expose for debugging
  (window as unknown as { lera: unknown }).lera = { terminal: state.terminal, ws: state.ws, ttyd: state.ttyd, state };

  console.log('[lera] Initialized');
  console.log('[lera] WebSocket URL:', wsUrl);
}

// Hosted headless sessions use the native full-grid stream. The canvas owns
// the play surface; HTML remains only for transient overlays and account tools.
// Callable repeatedly -- the profile switcher tears down the previous
// attachment (state.detachProfile) and calls this again to reattach to a
// different profile's port.
function attachProfile(container: HTMLElement, wsUrl: string, token: string, wsToken: string,
                       user: User, fileWsUrl?: string, onProfilesChanged?: () => void): void {
  state.workspaceMode = false;
  state.terminal = null;
  state.ttyd = null;
  state.channelManager = null;
  state.channels.clear();
  state.gridView?.dispose();
  state.gridShell?.dispose();
  state.fileManager?.destroy();
  state.fileManager = null;
  container.innerHTML = '';

  const app = document.createElement('div');
  app.id = 'app';
  app.className = 'grid-app';
  const surface = document.createElement('main');
  surface.id = 'grid-surface';
  app.appendChild(surface);
  container.appendChild(app);

  const socket = new LeraWebSocket(wsUrl);
  state.ws = socket;
  let shell: GridShell | null = null;
  const view = new GridView({
    container: surface,
    socket,
    onRoleChange: (role) => shell?.setRole(role),
  });
  state.gridView = view;

  const resolvedFileWsUrl = fileWsUrl || wsUrl;
  injectFileStyles();
  injectSettingsStyles();
  if (user.is_admin) injectAdminStyles();
  const openDisposablePanel = (
    selector: string,
    onClose: () => void,
    render: (finish: () => void) => Promise<void>,
  ) => {
    let closed = false;
    let disposed = false;
    const finish = () => {
      if (closed) return;
      closed = true;
      onClose();
    };
    const dispose = () => {
      if (disposed) return;
      disposed = true;
      const panel = document.querySelector(selector);
      document.dispatchEvent(new KeyboardEvent('keydown', {
        key: 'Escape', bubbles: true, cancelable: true,
      }));
      if (!closed) {
        panel?.remove();
        finish();
      }
    };
    void render(finish).catch(dispose);
    return dispose;
  };
  shell = new GridShell({
    container: app,
    focusGrid: () => view.focus(),
    requestRepaint: () => view.requestRepaint(),
    user: {
      displayName: user.display_name,
      email: user.email,
      isAdmin: user.is_admin,
    },
    openFiles: (onClose) => {
      const existing = state.fileManager;
      if (existing !== null) {
        let closed = false;
        return () => {
          if (closed) return;
          closed = true;
          existing.destroy();
          if (state.fileManager === existing) state.fileManager = null;
          onClose();
        };
      }
      let closed = false;
      const manager = new FileManager({
        container: app,
        wsUrl: resolvedFileWsUrl,
        token: wsToken,
        onClose: () => {
          if (closed) return;
          closed = true;
          if (state.fileManager === manager) state.fileManager = null;
          onClose();
        },
      });
      state.fileManager = manager;
      return () => {
        if (closed) return;
        manager.destroy();
        closed = true;
        if (state.fileManager === manager) state.fileManager = null;
        onClose();
      };
    },
    openSettings: (onClose) => openDisposablePanel('.settings-panel', onClose, (finish) =>
      renderSettingsPanel({
        container: document.body,
        token,
        userEmail: user.email,
        onClose: finish,
        onLogout: () => window.location.reload(),
        onProfilesChanged: onProfilesChanged ?? (() => {}),
      })),
    openAdmin: user.is_admin ? (onClose) => openDisposablePanel('.admin-panel', onClose, (finish) =>
      renderAdminPanel({
        container: document.body,
        token,
        onClose: finish,
      })) : undefined,
    onTakeControl: () => view.takeControl(),
    onLogout: () => {
      void logout(token).finally(() => window.location.reload());
    },
  });
  state.gridShell = shell;

  let gridReady = false;
  let gridFailed = false;
  let negotiationTimer: number | null = null;
  const focusGridOnWindowFocus = () => shell?.focusGridIfIdle();
  const removeWindowFocusListener = () => {
    window.removeEventListener('focus', focusGridOnWindowFocus);
  };
  const disposeGridShell = () => {
    shell?.dispose();
    const manager = state.fileManager;
    state.fileManager = null;
    manager?.destroy();
  };
  const clearNegotiationTimer = () => {
    if (negotiationTimer === null) return;
    clearTimeout(negotiationTimer);
    negotiationTimer = null;
  };
  // Full teardown for a profile switch (unlike useLegacy/failNegotiation,
  // which replace app's contents in place, this also removes app itself so
  // the next attachProfile() call starts from a clean container).
  const detach = () => {
    clearNegotiationTimer();
    removeWindowFocusListener();
    socket.disconnect();
    view.dispose();
    disposeGridShell();
    state.gridView = null;
    state.gridShell = null;
    app.remove();
  };
  state.detachProfile = detach;
  const useLegacy = () => {
    retireProfileSwitcher();
    clearNegotiationTimer();
    removeWindowFocusListener();
    socket.disconnect();
    view.dispose();
    disposeGridShell();
    state.gridView = null;
    state.gridShell = null;
    initLegacyTerminal(container, wsUrl, wsToken, false, fileWsUrl);
  };
  const failNegotiation = (message: string) => {
    if (gridFailed) return;
    gridFailed = true;
    retireProfileSwitcher();
    clearNegotiationTimer();
    removeWindowFocusListener();
    socket.disconnect();
    view.dispose();
    disposeGridShell();
    state.gridView = null;
    state.gridShell = null;
    showGridError(surface, message, useLegacy);
  };
  socket.on('open', () => {
    gridReady = false;
    clearNegotiationTimer();
    negotiationTimer = window.setTimeout(() => {
      failNegotiation('The server did not accept native grid mode.');
    }, 10_000);
  });
  socket.on('grid_ready', (message) => {
    if (!view.handleReady(message)) {
      failNegotiation('The server returned an invalid native grid handshake.');
      return;
    }
    gridReady = true;
    clearNegotiationTimer();
  });
  socket.on('grid_config', (message) => { void view.handleConfig(message); });
  socket.on('grid_feature_ready', (message) => {
    if (!view.handleFeatureReady(message)) {
      failNegotiation('The server returned an invalid image feature acknowledgement.');
    }
  });
  socket.on('grid_image_reset', (message) => view.handleImageReset(message));
  socket.on('grid_role', (message) => view.handleRole(message));
  socket.on('grid_pointer_result', (message) => view.handlePointerResult(message));
  socket.on('grid_cancel_interaction', (message) => view.handleCancelInteraction(message));
  socket.on('binary', (message) => { void view.handleBinary(message); });
  socket.on('close', () => {
    gridReady = false;
    clearNegotiationTimer();
    if (!gridFailed) view.resetForReconnect();
  });
  socket.on('error', (message) => {
    if (message.code === 'auth_failed' || message.code === 'auth_required') {
      clearToken();
      window.location.reload();
    } else if (!gridReady) {
      failNegotiation(message.message || message.code);
    } else {
      console.error(`[lera] Grid error ${message.code}: ${message.message}`);
    }
  });
  window.addEventListener('focus', focusGridOnWindowFocus);
  socket.connectGrid(wsToken);
  view.focus();

  (window as unknown as { lera: unknown }).lera = { grid: view, ws: socket, state };
  console.log('[lera] Native grid initialized');
  console.log('[lera] WebSocket URL:', wsUrl);
}

// Hosted mode initialization
async function initHostedMode(container: HTMLElement) {
  injectLoginStyles();

  // Check for OAuth callback
  const callback = parseAuthCallback();
  if (callback.token) {
    setToken(callback.token);
  }
  if (callback.error) {
    renderLoginScreen({
      container,
      error: callback.error,
    });
    return;
  }

  // Check for existing token
  let token = getToken();
  if (!token || isTokenExpired(token)) {
    // Show login screen
    renderLoginScreen({ container });
    return;
  }

  // Show loading
  showLoading(container);

  try {
    // Fetch user info
    const user = await fetchUser(token);
    void refreshTokenIfStale();
    state.token = token;
    state.user = user;

    // Check if pending approval
    if (user.status === 'pending') {
      renderLoginScreen({
        container,
        pending: true,
        onRetry: () => window.location.reload(),
        onLogout: () => {
          clearToken();
          window.location.reload();
        },
      });
      return;
    }

    // Check if suspended
    if (user.status === 'suspended') {
      renderLoginScreen({
        container,
        error: 'suspended',
      });
      return;
    }

    // Start container and get port
    let containerInfo;
    try {
      containerInfo = await startContainer(token);
    } catch (err) {
      console.error('Failed to start container:', err);
      container.innerHTML = `
        <div class="error-screen">
          <h2>Failed to start session</h2>
          <p>${escapeHtml(err instanceof Error ? err.message : 'Unknown error')}</p>
          <button onclick="window.location.reload()">Retry</button>
        </div>
      `;
      return;
    }

    // The credential for this user's OWN container, signed with a key derived
    // for them alone. Deliberately NOT `token`: that one is signed with the
    // manager's master secret and carries the admin claim, and sending it into
    // a container is what forced every container to hold the master key.
    //
    // No fallback to `token` when it is missing. An absent ws_token means the
    // manager predates this bundle, and a visible auth failure is the correct
    // outcome - falling back would silently reinstate exactly what this
    // replaced.
    const wsToken = containerInfo.ws_token ?? '';
    if (!wsToken) {
      console.error('[lera] the manager returned no ws_token; this session will fail to authenticate');
    }

    // Connect to WebSocket
    const workspaceMode = containerInfo.workspace_mode ?? false;
    if (workspaceMode) {
      const wsUrl = getHostedWebSocketUrl(containerInfo.port);
      initLegacyTerminal(container, wsUrl, wsToken, true,
        containerInfo.file_port ? getHostedWebSocketUrl(containerInfo.file_port) : undefined);
      return;
    }

    const openSettingsPanel = () => {
      injectSettingsStyles();
      renderSettingsPanel({
        container: document.body,
        token,
        userEmail: user.email,
        onClose: () => {},
        onLogout: () => {
          window.location.reload();
        },
        // refresh (defined below) re-lists profiles and redraws the bar; by
        // the time this callback actually runs (a user interaction inside
        // the panel), refresh has long since been assigned.
        onProfilesChanged: () => { void refresh(); },
      });
    };

    injectProfileBarStyles();

    // Distinguished from a genuinely empty list: a server that can't be
    // reached (or a route that doesn't exist yet) must not be treated the
    // same as a server that authoritatively says "zero profiles" -- see the
    // branch below.
    let profilesLoadFailed = false;
    let profiles = await listProfiles(token).catch((err) => {
      console.error('Failed to list profiles:', err);
      profilesLoadFailed = true;
      return [] as Profile[];
    });

    let active = selectInitialProfile(profiles, recallProfile(user.id));

    // Ports are leased to running profiles, so a stopped profile has port 0
    // and nothing to connect to. Starting it is therefore part of attaching,
    // not a separate act the user has to perform first.
    //
    // The server enforces the concurrency limit on that start and stops
    // whatever it has to (auto-switch), which is why the fresh list below is
    // not optional: it carries both this profile's new port AND the fact that
    // some other profile is no longer running.
    const attach = async (p: Profile) => {
      // Defense in depth: once the session has fallen back to legacy mode,
      // no click on any bar node (even one rendered before the fallback and
      // still sitting in the DOM) may reattach native grid mode.
      if (state.profileSwitcherRetired) return;

      let target = p;
      if (!target.port) {
        try {
          await startProfile(token, target.name);
          profiles = await listProfiles(token);
          const started = profiles.find((q) => q.name === target.name);
          // No port even after a successful start means the supervisor has
          // not picked the profile up yet. Say so rather than opening a
          // socket to port 0, which fails with nothing to explain it.
          if (!started?.port) {
            console.error(`[lera] profile ${target.name} started but has no port yet`);
            drawBar();
            return;
          }
          target = started;
        } catch (err) {
          console.error(`Failed to start profile ${target.name}:`, err);
          drawBar();
          return;
        }
      }

      if (state.profileSwitcherRetired) return;
      state.detachProfile?.();
      state.detachProfile = null;
      active = target;
      rememberProfile(user.id, target.name);
      const url = getHostedWebSocketUrl(target.port);
      attachProfile(container, url, token, wsToken, user, url, () => { void refresh(); });
      drawBar();
    };

    const refresh = async () => {
      if (state.profileSwitcherRetired) return;
      profiles = await listProfiles(token).catch(() => profiles);
      // Two reasons active may no longer be a valid attachment:
      //  - it was null to begin with (zero-profile bootstrap, see below) and
      //    the user may have just created their first profile from the
      //    settings panel;
      //  - it pointed at a real profile that has since been deleted (by this
      //    user or from another tab). `active` still holds the deleted
      //    Profile OBJECT in that case, so `!active` alone does not catch
      //    it -- the object is truthy even though its row is gone, and the
      //    socket is left pointed at a port the supervisor may hand to a
      //    brand-new profile next.
      const activeGone = active !== null && !profiles.some((p) => p.name === active?.name);
      if (!active || activeGone) {
        if (activeGone) forgetProfile(user.id);
        const next = selectInitialProfile(profiles, recallProfile(user.id));
        if (next) {
          void attach(next);
          return;
        }
        // No survivors either: tear down and fall back to the zero-profile
        // bootstrap state rather than leaving a zombie socket attached to a
        // now-deleted profile's port.
        attachBootstrap();
        return;
      }
      drawBar();
    };

    const drawBar = () => {
      if (state.profileSwitcherRetired) return;
      renderProfileBar({
        container,
        profiles,
        activeName: active?.name ?? null,
        onSelect: (name) => {
          const next = profiles.find((p) => p.name === name);
          if (next && next.name !== active?.name) void attach(next);
        },
        onStart: async (name) => {
          await startProfile(token, name).catch((err) => console.error(err));
          await refresh();
        },
        onStop: async (name) => {
          await stopProfile(token, name).catch((err) => console.error(err));
          await refresh();
        },
        onManage: () => openSettingsPanel(),
      });
    };

    // The server authoritatively says this user has zero profiles (or no
    // longer has any survivors after a deletion): still get a working
    // session on the container port, but draw the bar (just the "+" manage
    // button) so they have a way to create one; refresh() promotes off this
    // state automatically once a profile exists to attach to.
    const attachBootstrap = () => {
      state.detachProfile?.();
      state.detachProfile = null;
      active = null;
      attachProfile(container, getHostedWebSocketUrl(containerInfo.port), token, wsToken, user,
        undefined, () => { void refresh(); });
      drawBar();
    };

    // No third "profiles.length > 0 but active is falsy" branch: active is
    // only ever null here when profiles is empty (selectInitialProfile's
    // only `return null`), so that combination cannot occur on first load.
    if (active) {
      void attach(active);
    } else if (profilesLoadFailed) {
      // Couldn't determine whether this user has any profiles at all (no
      // profiles route yet, or the request failed outright) -- fall back to
      // the container's own port with no bar, exactly as before profiles
      // existed. Drawing a manage-only bar here would be presenting UI for a
      // feature the server may not even support.
      attachProfile(container, getHostedWebSocketUrl(containerInfo.port), token, wsToken, user);
    } else {
      attachBootstrap();
    }

    // The supervisor's view of the world changes without the browser asking,
    // so poll while the page is visible.
    state.profilePollTimer = window.setInterval(() => {
      if (document.visibilityState === 'visible') void refresh();
    }, 5000);

  } catch (err) {
    console.error('Auth error:', err);
    clearToken();
    renderLoginScreen({ container });
  }
}

// Initialize the application
function init() {
  const container = document.body;

  if (isHostedMode()) {
    // Hosted mode: require authentication
    initHostedMode(container);
  } else {
    // Standalone mode: direct WebSocket connection
    const wsUrl = getStandaloneWebSocketUrl();

    // Restore app structure for standalone
    container.innerHTML = `
      <div id="app">
        <header id="header">
          <h1>Lera</h1>
          <div class="status-bar">
            <span id="status" class="status disconnected">Disconnected</span>
            <span id="mud-status" class="mud-status disconnected">Not connected to MUD</span>
            <button id="files-btn" class="header-btn" title="Files">
              <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
                <path d="M10 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2h-8l-2-2z"/>
              </svg>
            </button>
            <a href="/docs.html" class="header-btn" title="Lua API Docs">
              <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
                <path d="M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z"/>
              </svg>
            </a>
          </div>
        </header>
        <main id="terminal"></main>
      </div>
    `;

    const terminalContainer = document.getElementById('terminal')!;
    initLegacyTerminal(terminalContainer.parentElement!, wsUrl);
  }
}

// Start when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
